import type { FrontendApi } from '@ory/kratos-client';
import { useContext, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  RouterProvider,
  redirect,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import { oryClient, oryWhoAmI } from '@revelio/auth';
import { history } from '@revelio/core';

import ABSignup from './app/pages/ab-signup';
import Login from './app/pages/login';
import LoginSSO from './app/pages/login-sso';
import Personalize from './app/pages/personalize';
import Recovery, { createFlow, getFlow } from './app/pages/recovery';
import Settings from './app/pages/settings';
import TrialExpired from './app/pages/trial-expired';
import { RedirectContext } from './redirect-context';
import { trackPageView } from './tracking';
import { isCreateAccountEnabled, isWilsonBrand } from './wilson-brand';

const defaultPath = isCreateAccountEnabled ? '/signup' : '/login';

function RouterRoot() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [, setRedirect] = useContext(RedirectContext);
  const redirect_uri = searchParams.get('redirect_uri');
  useEffect(() => {
    if (redirect_uri) {
      setRedirect(redirect_uri);
    }
  }, [redirect_uri, setRedirect]);
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return <Outlet />;
}

export { RouterProvider };

export const routes = [
  {
    index: true,
    element: <Navigate to={defaultPath} replace />,
  },
  {
    path: '/',
    element: <RouterRoot />,
    children: [
      {
        path: 'signup',
        element: isCreateAccountEnabled ? (
          <ABSignup />
        ) : (
          <Navigate to="/login" replace />
        ),
      },
      {
        // Returning trial users whose personalization_completed metadata is
        // empty get routed here (by the portal login flow and by the
        // dashboard's AuthGate) instead of the dashboard.
        path: 'personalize',
        element: isCreateAccountEnabled ? (
          <Personalize />
        ) : (
          <Navigate to="/login" replace />
        ),
        // The page needs the session's email + user id; no session, no page.
        // toSession, not oryWhoAmI: the latter is memoized for 2s, so right
        // after a login it can still answer with the cached logged-out
        // session and wrongly bounce the fresh arrival back to /login.
        loader: async () => {
          const { data: session } = await (oryClient as FrontendApi)
            .toSession()
            .catch(() => ({ data: undefined }));
          const email = session?.identity?.traits?.email;
          const userId = session?.identity?.id;
          if (!session?.active || !email || !userId) {
            return redirect('/login');
          }
          return { email, userId };
        },
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'login-sso',
        element: isWilsonBrand ? (
          <Navigate to="/login" replace />
        ) : (
          <LoginSSO />
        ),
      },
      {
        path: 'recovery',
        element: <Recovery />,
        loader: async ({ request }: { request: Request }) => {
          // check if valid user session
          const whoAmI = await oryWhoAmI();
          if (whoAmI.active) {
            return redirect('login');
          }

          const url = new URL(request.url);
          const flowId = url.searchParams.get('flow');

          // get flow object for flowId
          if (flowId) {
            const { data: existingFlow } = await getFlow(flowId);
            // An expired/garbage ?flow= comes back 410 — getFlow swallows the
            // throw and hands back the error, leaving no usable flow. Fall
            // through and seat a fresh one instead of a dead screen.
            if (existingFlow?.ui) {
              return { validAuth: whoAmI.active, flow: existingFlow };
            }
          }

          // doesn't have a flow id, so make one — keep the existing params
          // (`s`, `theme`, …) alongside the flow id
          const { data: flow } = await createFlow();
          const params = new URLSearchParams(url.search);
          params.set('flow', flow.id);
          history.replace({
            pathname: history.location.pathname,
            search: `?${params.toString()}`,
          });
          return { validAuth: whoAmI.active, flow };
        },
      },
      {
        path: 'settings',
        element: <Settings />,
      },
      {
        path: 'trial-expired',
        element: isWilsonBrand ? (
          <Navigate to="/login" replace />
        ) : (
          <TrialExpired />
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={defaultPath} replace />,
  },
];
